import { createFileRoute, Link } from '@tanstack/react-router';
import { useJob } from '@/hooks/useJobs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  AlertCircle,
  Loader2,
  ExternalLink,
  Users,
  MapPin,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { jobsApi } from '@/lib/api/jobs.api';
import type { AssignedCustomer } from '@repo/types';

export const Route = createFileRoute('/admin/jobs/$id/assigned-customers')({
  component: AssignedCustomersPage,
});

function AssignedCustomersPage() {
  const { id } = Route.useParams();
  const { data: job, isLoading, error } = useJob(id);

  // Fetch assigned customers
  const { data: assignedCustomers = [], isLoading: isLoadingAssignedCustomers } = useQuery({
    queryKey: ['job', id, 'assigned-customers'],
    queryFn: () => jobsApi.getAssignedCustomers(id),
    enabled: !!job,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertCircle className="size-3" />
          <AlertDescription>
            {error instanceof Error ? error.message : 'Failed to load job details. Please try again.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between pb-2 border-b border-border/50">
        <div className="flex items-center gap-3">
          <Users className="h-5 w-5 text-primary" />
          <h3 className="text-xl font-bold tracking-tight">Assigned Customers ({assignedCustomers.length})</h3>
        </div>
        <Link
          to="/admin/jobs/$id/candidates"
          params={{ id }}
        >
          <Button
            size="sm"
            variant="outline"
            className="gap-2"
          >
            <Users className="h-4 w-4" />
            Assign Customer
          </Button>
        </Link>
      </div>

      <div>
        {isLoadingAssignedCustomers ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : assignedCustomers.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm font-medium mb-1">No customers assigned yet</p>
            <p className="text-xs mb-4">Start by assigning a customer to this job</p>
            <Link
              to="/admin/jobs/$id/candidates"
              params={{ id }}
            >
              <Button
                size="sm"
                variant="outline"
                className="gap-2"
              >
                <Users className="h-4 w-4" />
                Assign First Customer
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {assignedCustomers.map((assignment: AssignedCustomer) => (
              <div
                key={assignment.recommendationId}
                className="group p-5 rounded-xl border border-border/50 hover:border-primary/40 hover:bg-gradient-to-br hover:from-primary/5 hover:to-transparent transition-all shadow-sm hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-3 min-w-0">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <Link
                          to="/admin/customers/$id"
                          params={{ id: assignment.customerId }}
                          className="font-semibold text-base hover:text-primary transition-colors line-clamp-2 block"
                        >
                          {assignment.customerName}
                        </Link>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          <Badge
                            variant={
                              assignment.recommendationStatus === 'applied'
                                ? 'default'
                                : assignment.recommendationStatus === 'viewed'
                                ? 'secondary'
                                : 'outline'
                            }
                            className="text-xs capitalize"
                          >
                            {assignment.recommendationStatus}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                      {assignment.customerEmail && (
                        <div className="flex items-center gap-1.5">
                          <span>{assignment.customerEmail}</span>
                        </div>
                      )}
                      {assignment.customerLocation && (
                        <div className="flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5" />
                          <span>{assignment.customerLocation}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1 border-t">
                      {assignment.assignedByName && (
                        <span>Assigned by <span className="font-medium">{assignment.assignedByName}</span></span>
                      )}
                    </div>
                  </div>
                  <Link
                    to="/admin/customers/$id"
                    params={{ id: assignment.customerId }}
                    className="shrink-0"
                  >
                    <Button variant="ghost" size="sm" className="gap-2">
                      <span className="hidden group-hover:inline text-xs">View</span>
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

